import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useFetch from '../hooks/useFetch';
import { getGalleryItem } from '../services/api';

const ProjectPage = () => {
  const { id } = useParams();
  const fetchFn = useCallback(() => getGalleryItem(id), [id]);
  const { data: project, loading, error } = useFetch(fetchFn);
  const [mainImage, setMainImage] = useState(null);
  const [announcedAlt, setAnnouncedAlt] = useState('');

  useEffect(() => {
    const timeout = setTimeout(
      () => setAnnouncedAlt(mainImage?.alt || ''),
      400
    );
    return () => clearTimeout(timeout);
  }, [mainImage]);

  useEffect(() => {
    if (project) {
      setMainImage({ src: project.image, alt: project.alt });
      document.title = `${project.title} in ${project.location} – PaveMend`;

      let metaDesc = document.querySelector('meta[name="description"]');
      const created = !metaDesc;
      if (created) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = `See our ${project.title} project in ${project.location}. PaveMend delivers expert hardscaping across South East London. Get a free quote.`;

      let canonical = document.querySelector('link[rel="canonical"]');
      if (canonical)
        canonical.href = `https://pavemend.co.uk/projects/${project.id}`;

      return () => {
        document.title =
          'PaveMend – Driveways, Patios & Paving in South East London';
        if (created && metaDesc.parentNode) document.head.removeChild(metaDesc);
        else if (metaDesc)
          metaDesc.content =
            "PaveMend is South East London's trusted hardscaping contractor. Expert driveway, patio, and paving installations with a free quote.";
        if (canonical) canonical.href = 'https://pavemend.co.uk/';
      };
    }
  }, [project]);

  if (loading) {
    return (
      <div className="project-details project-details--loading">
        <div className="project-details__skeleton">
          <div className="project-details__skeleton-hero" aria-hidden="true" />
          <div className="project-details__skeleton-body">
            <div
              className="project-details__skeleton-main"
              aria-hidden="true"
            />
            <div
              className="project-details__skeleton-strip"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="project-details project-details--error">
        <h2>Project Not Found</h2>
        <p>We couldn't find the project you're looking for.</p>
        <Link to="/#gallery" className="btn-primary">
          Back to Gallery
        </Link>
      </div>
    );
  }

  const allImages = [
    { src: project.image, alt: project.alt },
    ...(project.additional_images || []).map((img, index) => ({
      src: img,
      alt: `${project.title} – photo ${index + 2}`,
    })),
  ];

  return (
    <article className="project-details">
      {/* ── Full-width hero banner ── */}
      <div className="project-details__hero">
        <img
          src={project.image}
          alt={project.alt}
          className="project-details__hero-img"
          loading="eager"
          fetchPriority="high"
        />
        <div className="project-details__hero-overlay" aria-hidden="true" />

        {/* Back nav inside the hero */}
        <Link to="/#gallery" className="project-details__back">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          All Projects
        </Link>

        {/* Title overlaid at bottom */}
        <div className="project-details__hero-caption">
          <p className="project-details__location">{project.location}</p>
          <h1 className="project-details__title">{project.title}</h1>
        </div>
      </div>

      {/* ── Gallery: large image + vertical thumbnail strip ── */}
      <div className="project-details__gallery-section">
        <div className="project-details__gallery-inner">
          {/* Main switchable image */}
          <div className="project-details__main-image-wrapper">
            <img
              key={mainImage?.src}
              src={mainImage?.src}
              alt={mainImage?.alt}
              className="project-details__main-image"
              loading="lazy"
            />
            <p className="sr-only" role="status">
              {announcedAlt}
            </p>
          </div>

          {/* Vertical thumbnail strip */}
          <div className="project-details__strip">
            {allImages.map((img, idx) => (
              <button
                key={idx}
                className={`project-details__thumb ${
                  mainImage?.src === img.src ? 'is-active' : ''
                }`}
                onClick={() => setMainImage(img)}
                aria-label={`View photo ${idx + 1}`}
                aria-pressed={mainImage?.src === img.src}
              >
                <img src={img.src} alt={img.alt} loading="lazy" />
                {mainImage?.src === img.src && (
                  <span
                    className="project-details__thumb-bar"
                    aria-hidden="true"
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="project-details__cta-section">
        <div className="project-details__cta-inner">
          <div className="project-details__cta-text">
            <p className="project-details__cta-eyebrow">Inspired?</p>
            <h2 className="project-details__cta-heading">
              Let's build something for your property
            </h2>
            <p className="project-details__cta-sub">
              Free quote, no obligation. We cover all of South East London.
            </p>
          </div>
          <Link to="/booking" className="btn-primary project-details__cta-btn">
            Get a Free Quote
          </Link>
        </div>
      </div>
    </article>
  );
};

export default ProjectPage;
